import { useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { Button, Card, FormField, PageHeader, Select, inputClass } from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateFormTemplate,
  type DeviceCategory,
  type FormTargetKind,
} from "@/hooks/use-company-form-templates";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";

export const Route = createFileRoute("/_company/company/forms/new")({
  component: NewFormTemplatePage,
});

const TARGET_KINDS: FormTargetKind[] = ["device_test", "premise_inspection"];
const DEVICE_CATEGORIES: DeviceCategory[] = ["testable_assembly", "air_gap", "non_testable_device"];

/**
 * Title is the human identity; slug is the URL-safe identity derived from the
 * title keystroke-by-keystroke until the author edits the slug field directly,
 * after which the manual value wins (slugs are immutable post-create).
 */
function deriveSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

interface FieldErrors {
  title?: string;
  slug?: string;
  target_kind?: string;
  device_category?: string;
}

/**
 * Creates a company supplemental-form template family (identity only — no
 * versions). The first draft document arrives in the builder, which this page
 * hands off to on success. device_category is fixed at create (the company
 * spec has no template PATCH — conflict #3); it is rendered read-only in the
 * builder's TemplateSettings afterward.
 */
function NewFormTemplatePage() {
  const { t } = useTranslation(["company", "common", "form-templates"]);
  const navigate = useNavigate();
  const isAdmin = useIsCompanyAdmin();
  const toasts = useToast();
  const createMutation = useCreateFormTemplate();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [targetKind, setTargetKind] = useState<FormTargetKind | "">("");
  const [deviceCategory, setDeviceCategory] = useState<DeviceCategory | "">("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [slugConflict, setSlugConflict] = useState<string | null>(null);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugTouched) setSlug(deriveSlug(value));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSlugConflict(null);
    const nextErrors: FieldErrors = {};
    if (title.trim() === "") nextErrors.title = t("forms.titleRequired");
    if (slug.trim() === "") nextErrors.slug = t("forms.slugRequired");
    if (targetKind === "") nextErrors.target_kind = t("forms.targetKindRequired");
    if (targetKind === "device_test" && deviceCategory === "")
      nextErrors.device_category = t("forms.deviceCategoryRequired");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || targetKind === "") return;

    createMutation.mutate(
      {
        title: title.trim(),
        slug: slug.trim(),
        target_kind: targetKind,
        ...(targetKind === "device_test" && deviceCategory !== ""
          ? { device_category: deviceCategory }
          : {}),
      },
      {
        onSuccess: (created) => {
          toasts.success("company:forms.createSuccess");
          void navigate({
            to: "/company/forms/$templateId/builder",
            params: { templateId: created.id },
          });
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === "form_template_slug_conflict") {
            setSlugConflict(t("forms.slugConflict"));
          } else {
            toasts.apiError(err instanceof ApiError ? err.code : undefined);
          }
        },
      },
    );
  };

  // ft_company_insert gates the create on the admin role, so a tester
  // typing this route straight in meets the same refusal the CTA already
  // hides — stated here rather than as a 403 toast after the fact.
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl space-y-6 p-8">
        <PageHeader title={t("forms.createDialogTitle")} />
        <Card>
          <p className="text-sm text-on-surface">
            {t("form-templates:builder.guard.companyAdminOnly")}
          </p>
          <Link
            to="/company/forms"
            className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
          >
            {t("forms.title")}
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <PageHeader title={t("forms.createDialogTitle")} />
      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label={t("forms.titleLabel")} required error={errors.title}>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField
            label={t("forms.slugLabel")}
            required
            error={errors.slug ?? slugConflict ?? undefined}
            hint={t("forms.slugHint")}
          >
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              className={inputClass}
            />
          </FormField>
          <FormField label={t("forms.targetKindLabel")} required error={errors.target_kind}>
            <Select
              value={targetKind}
              placeholder={t("forms.targetKindLabel")}
              options={TARGET_KINDS.map((kind) => ({
                value: kind,
                label: t(`forms.targetKind.${kind}`),
              }))}
              onChange={(e) => {
                // Narrowing cast: the option values are exactly the
                // FormTargetKind enum members declared in TARGET_KINDS.
                const next = e.target.value as FormTargetKind;
                setTargetKind(next);
                if (next !== "device_test") setDeviceCategory("");
              }}
            />
          </FormField>
          {targetKind === "device_test" && (
            <FormField
              label={t("forms.deviceCategoryLabel")}
              required
              error={errors.device_category}
              hint={t("forms.deviceCategoryHint")}
            >
              <Select
                value={deviceCategory}
                placeholder={t("forms.deviceCategoryLabel")}
                options={DEVICE_CATEGORIES.map((category) => ({
                  value: category,
                  label: t(`device_category.${category}`, { ns: "vocabulary" }),
                }))}
                onChange={(e) =>
                  // Narrowing cast: the option values are exactly the
                  // DeviceCategory enum members declared in DEVICE_CATEGORIES.
                  setDeviceCategory(e.target.value as DeviceCategory)
                }
              />
            </FormField>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void navigate({ to: "/company/forms" })}
            >
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {t("forms.newTemplate")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
